import { type ApolloClient, gql, type NormalizedCacheObject } from '@apollo/client/core';
import type { VPResult } from '../../VPResult';
import { type InternalApiError, toApiErrors } from '../errors';
import { CONNECTION_METADATA_FIELDS, type ConnectionArguments } from '../../connection/Api';
import * as Ordering from '../../Shared/Ordering';
import * as EmailDelivery from './EmailDelivery';
import type * as EmailDeliveryFilterArguments from './EmailDeliveryFilterArguments';
import type { EmailDeliveryGroupBy } from './EmailDeliveryGroupBy';
import type { EmailDeliverySortField } from './EmailDeliverySortField';
import * as EmailDeliverySummary from './EmailDeliverySummary';
import { INTERNAL_EMAIL_DELIVERY_FIELDS, INTERNAL_EMAIL_DELIVERY_SUMMARY_FIELDS } from './fragments';
import { ACCOUNT_AGNOSTIC, forAccount } from '../../accountContext';

/**
 * Email deliverability for VenuePilot staff: the same sends read two ways — as a filterable,
 * cursor-paged list of individual deliveries, each carrying the terminal SendGrid event behind a
 * failure, and as a grouped rollup of counts and failure rates over the *same* filter.
 *
 * These live on the internal (VP staff) schema rather than AppSchema (ADR-009): they read every
 * account's mail regardless of tenancy, which is exactly what should not sit in a published schema.
 * Auth is the superadmin Rails session cookie — a bearer token never satisfies the gate, and it only
 * works in the Rails-embedded admin against a same-origin `/graphql`, never the standalone dev
 * server — so both reads go through `internalGqlApiClient`.
 *
 * A non-superadmin caller is rejected with HTTP 401 before the query is parsed, which Apollo *throws*
 * rather than returning a payload. Every read here therefore catches and classifies via `toApiErrors`, so
 * callers can branch on `isUnauthorized` and say "VenuePilot staff only" instead of rendering an
 * empty table — and so an expected authorization outcome does not reach Sentry as an exception.
 * **Nothing here throws.**
 *
 * Read-only: a deliverability report changes nothing, so there is no mutation here and no
 * `MutationResult` counterpart.
 *
 * Four contract facts worth knowing before building a page on this, none of them visible in the
 * schema itself:
 *
 * - `EmailDeliveryConnection` carries **no `totalCount`**. A row count can only come from the
 *   summary's `total`, so a screen that wants both a table and a count needs both reads.
 * - The list is capped server-side at **15 rows a page**, and that is also its default. A caller
 *   asking for `first: 50` gets 15 and a `hasNextPage`, not an error — so page through the cursors
 *   rather than trying to widen the page.
 * - The summary returns at most the **1000 largest groups**, while its `total` counts every matched
 *   delivery. Summing the groups can therefore come out below `total` on a broad read; `total` is
 *   the figure to render.
 * - The two enums disagree on wire casing — `EmailDeliveryState`'s values are lowercase, while
 *   `EmailDeliveryGroupBy`'s are SCREAMING_CASE. Pass the exported members rather than literals,
 *   especially where a filter round-trips through a URL query parameter.
 */

const INTERNAL_EMAIL_DELIVERIES_QUERY = gql`
  query InternalEmailDeliveries($after: String, $before: String, $first: Int, $last: Int, $filter: EmailDeliveryFilterArguments, $sort: [OrderingArgument!]) {
    emailDeliveries(after: $after, before: $before, first: $first, last: $last, filter: $filter, sort: $sort) {
      edges {
        cursor
        node {
          ...InternalEmailDeliveryFields
        }
      }
      pageInfo {
        ...ConnectionMetadataFields
      }
    }
  }
  ${INTERNAL_EMAIL_DELIVERY_FIELDS}
  ${CONNECTION_METADATA_FIELDS}
`;

const INTERNAL_EMAIL_DELIVERY_SUMMARY_QUERY = gql`
  query InternalEmailDeliverySummary($filter: EmailDeliveryFilterArguments, $groupBy: EmailDeliveryGroupByEnum!) {
    emailDeliverySummary(filter: $filter, groupBy: $groupBy) {
      ...InternalEmailDeliverySummaryFields
    }
  }
  ${INTERNAL_EMAIL_DELIVERY_SUMMARY_FIELDS}
`;

/**
 * `allAccounts` defaults to false server-side, takes precedence over `accountId` when true, and both
 * reads funnel through one scope helper that **rejects** the query when it is false with no
 * `accountId` — so it has to be derived here rather than left to callers.
 *
 * Three cases, not two. An account id built with `Number(route.query.…)` arrives as `NaN` from a
 * hand-edited `?accountId=abc`. Deriving from truthiness (`!accountId`) would read that as "no
 * account given" and widen to every account's mail — the exact escalation this schema exists to
 * remove. A present-but-unusable id therefore fails closed: no `accountId`, `allAccounts` still
 * false, and the backend rejects the unscoped read.
 *
 * Deliberately a duplicate of `Internal/Order/Api.ts`'s derivation rather than a shared helper:
 * extracting one would mean reworking the order reads from a merge request that is not about orders,
 * and two callers is not yet a pattern. Extract at the third.
 *
 * Shared by both reads below, so a rollup can never be scoped differently from the rows it totals —
 * a summary scoped differently would report failure rates for deliveries the table never shows.
 */
const toGqlFilter = (filter?: EmailDeliveryFilterArguments.EmailDeliveryFilterArguments) => {
  const requested = filter?.accountId !== undefined;
  const usable = Number.isInteger(filter?.accountId) && filter!.accountId! > 0;

  return {
    ...(filter ?? {}),
    accountId: usable ? filter!.accountId : undefined,
    allAccounts: !requested,
  };
};

/** The account both reads run in, derived from the same filter, branching as `Internal/Order` does. */
const filterContext = (filter?: EmailDeliveryFilterArguments.EmailDeliveryFilterArguments) => {
  const accountId = filter?.accountId;

  if (Number.isInteger(accountId) && accountId! > 0) {
    return forAccount(accountId!);
  }

  if (accountId !== undefined) {
    return undefined;
  }

  return ACCOUNT_AGNOSTIC;
};

export const fetchEmailDeliveries = async (
  client: ApolloClient<NormalizedCacheObject>,
  {
    pagination,
    filter,
    sort,
  }: {
    pagination?: ConnectionArguments;
    filter?: EmailDeliveryFilterArguments.EmailDeliveryFilterArguments;
    sort?: Ordering.Ordering<EmailDeliverySortField>[];
  } = {},
): Promise<VPResult<EmailDelivery.EmailDeliveryConnection, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_EMAIL_DELIVERIES_QUERY,
      // A report answered from the cache would keep showing a bounce the operator has since chased
      // down, which is the one thing this screen exists to get right.
      fetchPolicy: 'network-only',
      variables: {
        ...(pagination ?? {}),
        filter: toGqlFilter(filter),
        // An empty sort is how the resolver's own default (newest first, with an id tiebreaker for
        // deterministic cursor pages) is requested; `null` would be rejected against the input type.
        sort: sort ?? [],
      },
      context: filterContext(filter),
    });

    // `emailDeliveries` is non-null on the schema, so this is only reachable if a resolver
    // soft-fails — resolving it as an empty page would render "no deliveries" and hide the failure.
    if (!data?.emailDeliveries) {
      return { success: false, errors: [{ path: '', message: 'Failed to fetch email deliveries.' }] };
    }

    return { success: true, data: EmailDelivery.fromGqlConnection(data.emailDeliveries) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};

/**
 * `groupBy` is required rather than defaulting to a dimension: the schema requires it, and choosing
 * one here would put the report's editorial decision in the API layer.
 */
export const fetchEmailDeliverySummary = async (
  client: ApolloClient<NormalizedCacheObject>,
  {
    groupBy,
    filter,
  }: {
    groupBy: EmailDeliveryGroupBy;
    filter?: EmailDeliveryFilterArguments.EmailDeliveryFilterArguments;
  },
): Promise<VPResult<EmailDeliverySummary.EmailDeliverySummary, InternalApiError[]>> => {
  try {
    const { data } = await client.query({
      query: INTERNAL_EMAIL_DELIVERY_SUMMARY_QUERY,
      fetchPolicy: 'network-only',
      variables: { groupBy, filter: toGqlFilter(filter) },
      context: filterContext(filter),
    });

    // Same reasoning as the list read: non-null on the schema, so an absent report is a backend
    // fault. Resolving it as a zeroed one would state a 0% failure rate over a failed read.
    if (!data?.emailDeliverySummary) {
      return {
        success: false,
        errors: [{ path: '', message: 'Failed to fetch the email deliverability summary.' }],
      };
    }

    return { success: true, data: EmailDeliverySummary.fromGql(data.emailDeliverySummary) };
  } catch (error) {
    return { success: false, errors: toApiErrors(error) };
  }
};
